import React from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { usePageTitle } from '../../components/common/PageTitleContext'
import {
  createPrepCategory,
  deletePrepCategory,
  fetchPrepCategories,
  updatePrepCategory,
  type PrepCategoryWithCount,
} from '../../api/prep'

const DEFAULT_CATEGORY_SLUG = 'everything-else'

type ToastState = {
  open: boolean
  severity: 'success' | 'error'
  message: string
}

const PrepCategoryManagerPage: React.FC = () => {
  usePageTitle('Prep categories')

  const [categories, setCategories] = React.useState<PrepCategoryWithCount[]>([])
  const [loading, setLoading] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [toast, setToast] = React.useState<ToastState | null>(null)

  const [createName, setCreateName] = React.useState('')
  const [createSaving, setCreateSaving] = React.useState(false)

  const [editDialog, setEditDialog] = React.useState<{
    open: boolean
    category: PrepCategoryWithCount | null
    name: string
    saving: boolean
  }>({ open: false, category: null, name: '', saving: false })

  const [deleteDialog, setDeleteDialog] = React.useState<{
    open: boolean
    category: PrepCategoryWithCount | null
    saving: boolean
  }>({ open: false, category: null, saving: false })

  const [reassignDialog, setReassignDialog] = React.useState<{
    open: boolean
    category: PrepCategoryWithCount | null
    onlyCount: number
    replacementId: number
  }>({ open: false, category: null, onlyCount: 0, replacementId: 0 })

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false)
  const [bulkDeleting, setBulkDeleting] = React.useState(false)
  const reassignResolverRef = React.useRef<((value: number | null) => void) | null>(null)

  const loadCategories = React.useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const response = await fetchPrepCategories({ scope: 'global', includeEmpty: true })
      const list = Array.isArray(response.categories) ? response.categories : []
      setCategories(list)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load categories.'
      setLoadError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadCategories()
  }, [loadCategories])

  React.useEffect(() => {
    setSelectedIds((prev) => {
      const available = new Set(categories.map((category) => category.id))
      const next = new Set<number>()
      prev.forEach((id) => {
        if (available.has(id)) {
          next.add(id)
        }
      })
      return next
    })
  }, [categories])

  const getDefaultReplacementId = React.useCallback(
    (targetId?: number | null) => {
      const options = categories.filter((category) => category.id !== targetId)
      if (options.length === 0) {
        return 0
      }
      const preferred = options.find((category) => category.slug === DEFAULT_CATEGORY_SLUG)
      return preferred?.id ?? options[0]?.id ?? 0
    },
    [categories],
  )

  const replacementOptions = React.useMemo(() => {
    const targetId = reassignDialog.category?.id
    return categories.filter((category) => category.id !== targetId)
  }, [categories, reassignDialog.category?.id])

  const defaultReplacementId = React.useMemo(() => {
    return getDefaultReplacementId(reassignDialog.category?.id ?? null)
  }, [getDefaultReplacementId, reassignDialog.category?.id])

  const closeToast = () => {
    setToast(null)
  }

  const notify = (message: string, severity: ToastState['severity']) => {
    setToast({ open: true, severity, message })
  }

  const handleCreate = async () => {
    const trimmed = createName.trim()
    if (!trimmed || createSaving) {
      return
    }
    setCreateSaving(true)
    try {
      await createPrepCategory(trimmed)
      setCreateName('')
      await loadCategories()
      notify('Category created.', 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create category.'
      notify(message, 'error')
    } finally {
      setCreateSaving(false)
    }
  }

  const openEdit = (category: PrepCategoryWithCount) => {
    setEditDialog({ open: true, category, name: category.name ?? '', saving: false })
  }

  const closeEdit = () => {
    setEditDialog({ open: false, category: null, name: '', saving: false })
  }

  const handleEditSave = async () => {
    if (!editDialog.category || editDialog.saving) {
      return
    }
    const trimmed = editDialog.name.trim()
    if (!trimmed) {
      notify('Category name is required.', 'error')
      return
    }
    setEditDialog((prev) => ({ ...prev, saving: true }))
    try {
      await updatePrepCategory(editDialog.category.id, trimmed)
      await loadCategories()
      notify('Category updated.', 'success')
      closeEdit()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update category.'
      notify(message, 'error')
      setEditDialog((prev) => ({ ...prev, saving: false }))
    }
  }

  const openDelete = (category: PrepCategoryWithCount) => {
    setDeleteDialog({ open: true, category, saving: false })
  }

  const closeDelete = () => {
    setDeleteDialog({ open: false, category: null, saving: false })
  }

  const requestReassign = (category: PrepCategoryWithCount, onlyCount: number) => {
    return new Promise<number | null>((resolve) => {
      reassignResolverRef.current = resolve
      setReassignDialog({
        open: true,
        category,
        onlyCount,
        replacementId: getDefaultReplacementId(category.id),
      })
    })
  }

  const closeReassign = (result: number | null) => {
    if (reassignResolverRef.current) {
      reassignResolverRef.current(result)
      reassignResolverRef.current = null
    }
    setReassignDialog({ open: false, category: null, onlyCount: 0, replacementId: 0 })
  }

  const handleReassignCancel = () => {
    closeReassign(null)
  }

  const handleReassignConfirm = () => {
    const replacementId = reassignDialog.replacementId || defaultReplacementId
    closeReassign(replacementId > 0 ? replacementId : null)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.category || deleteDialog.saving) {
      return
    }
    setDeleteDialog((prev) => ({ ...prev, saving: true }))
    try {
      await deletePrepCategory(deleteDialog.category.id)
      await loadCategories()
      notify('Category deleted.', 'success')
      closeDelete()
    } catch (error) {
      const data = (error as { data?: unknown })?.data
      const code = data && typeof data === 'object' && 'code' in data ? String(data.code) : ''
      if (code === 'rpa_prep_category_reassign_required' && deleteDialog.category) {
        const countValue =
          data && typeof data === 'object' && 'only_category_count' in data
            ? Number((data as { only_category_count?: unknown }).only_category_count ?? 0)
            : 0
        closeDelete()
        const replacementId = await requestReassign(
          deleteDialog.category,
          Number.isFinite(countValue) ? countValue : 0,
        )
        if (!replacementId) {
          notify('Delete cancelled.', 'error')
          return
        }
        try {
          await deletePrepCategory(deleteDialog.category.id, replacementId)
          await loadCategories()
          notify('Category deleted and items reassigned.', 'success')
        } catch (reassignError) {
          const message =
            reassignError instanceof Error ? reassignError.message : 'Unable to delete category.'
          notify(message, 'error')
        }
        return
      }
      const message = error instanceof Error ? error.message : 'Unable to delete category.'
      notify(message, 'error')
      setDeleteDialog((prev) => ({ ...prev, saving: false }))
    }
  }

  const toggleSelected = (categoryId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const openBulkDialog = () => {
    setBulkDialogOpen(true)
  }

  const closeBulkDialog = () => {
    setBulkDialogOpen(false)
  }

  const handleBulkDeleteConfirm = async () => {
    if (bulkDeleting) {
      return
    }
    setBulkDialogOpen(false)
    setBulkDeleting(true)
    const selected = categories.filter((category) => selectedIds.has(category.id))
    let deletedCount = 0
    try {
      for (const category of selected) {
        if (category.slug === DEFAULT_CATEGORY_SLUG) {
          continue
        }
        try {
          await deletePrepCategory(category.id)
          deletedCount += 1
        } catch (error) {
          const data = (error as { data?: unknown })?.data
          const code = data && typeof data === 'object' && 'code' in data ? String(data.code) : ''
          if (code === 'rpa_prep_category_reassign_required') {
            const countValue =
              data && typeof data === 'object' && 'only_category_count' in data
                ? Number((data as { only_category_count?: unknown }).only_category_count ?? 0)
                : 0
            const replacementId = await requestReassign(
              category,
              Number.isFinite(countValue) ? countValue : 0,
            )
            if (!replacementId) {
              notify('Bulk delete cancelled.', 'error')
              return
            }
            await deletePrepCategory(category.id, replacementId)
            deletedCount += 1
          } else {
            const message = error instanceof Error ? error.message : 'Unable to delete category.'
            notify(message, 'error')
            return
          }
        }
      }
      await loadCategories()
      setSelectedIds(new Set())
      notify(`Deleted ${deletedCount} categories.`, 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete categories.'
      notify(message, 'error')
    } finally {
      setBulkDeleting(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h2">
          Prep categories
        </Typography>
        <Typography color="text.secondary">
          Create, rename, and delete global prep categories. Categories only used for prep items.
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" component="h3">
            Add category
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Category name"
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createSaving || !createName.trim()}
              sx={{ minWidth: 160 }}
            >
              Add category
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" component="h3">
              Categories
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="error"
                onClick={openBulkDialog}
                disabled={selectedIds.size === 0 || bulkDeleting}
              >
                Bulk delete
              </Button>
              <Button variant="text" onClick={loadCategories} disabled={loading || bulkDeleting}>
                Refresh
              </Button>
            </Stack>
          </Stack>

          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="right">Items</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">Loading categories...</Typography>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No categories yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => {
                  const isProtected = category.slug === DEFAULT_CATEGORY_SLUG
                  const isSelected = selectedIds.has(category.id)
                  return (
                    <TableRow key={category.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleSelected(category.id)}
                          disabled={isProtected || bulkDeleting}
                          inputProps={{ 'aria-label': `Select ${category.name}` }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.slug}</TableCell>
                      <TableCell align="right">{category.count ?? 0}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => openEdit(category)}
                            disabled={isProtected || bulkDeleting}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => openDelete(category)}
                            disabled={isProtected || bulkDeleting}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              onClick={openBulkDialog}
              disabled={selectedIds.size === 0 || bulkDeleting}
            >
              Bulk delete
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={editDialog.open} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit category</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              Update the category name. The slug will update automatically.
            </Typography>
            <TextField
              label="Category name"
              value={editDialog.name}
              onChange={(event) => setEditDialog((prev) => ({ ...prev, name: event.target.value }))}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editDialog.saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete category</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <Typography>
              Delete "{deleteDialog.category?.name}"? This action cannot be undone.
            </Typography>
            <Typography color="text.secondary">
              If any prep items only have this category, you will be asked to move them.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialog.saving}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reassignDialog.open} onClose={handleReassignCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Move items before delete</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Typography>
              {reassignDialog.onlyCount} items only use "{reassignDialog.category?.name}". Choose a
              replacement category.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="prep-category-reassign-label">Move items to</InputLabel>
              <Select
                labelId="prep-category-reassign-label"
                value={reassignDialog.replacementId || defaultReplacementId}
                label="Move items to"
                onChange={(event) =>
                  setReassignDialog((prev) => ({
                    ...prev,
                    replacementId: Number(event.target.value) || 0,
                  }))
                }
              >
                {replacementOptions.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReassignCancel}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReassignConfirm}
            disabled={replacementOptions.length === 0}
          >
            Move and delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk delete categories</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <Typography>Delete the selected categories? This action cannot be undone.</Typography>
            <Typography color="text.secondary">
              If any selected categories require reassignment, you will be prompted during deletion.
            </Typography>
            <Stack spacing={0.5}>
              {categories
                .filter((category) => selectedIds.has(category.id))
                .map((category) => (
                  <Typography key={category.id} variant="body2">
                    {category.name}
                  </Typography>
                ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDeleteConfirm}
            disabled={bulkDeleting}
          >
            Delete selected
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast?.open)}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={closeToast} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Stack>
  )
}

export default PrepCategoryManagerPage
